import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { getApiConfig } from "@/services/api.service";
import { getCurrentUser } from "@/stores/auth-store";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { applyOrgAuditBatch } from "./apply-org-audit-batch";
import {
	type AuditLogMessage,
	parseAuditLogMessage,
} from "./audit-log-message";

const GUID_PATTERN =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MAX_RECONNECT_DELAY_MS = 30_000;
const BATCH_DELAY_MS = 300;

function liveUpdateWebSocketUrl(organizationId: string): string {
	const webSocket = new URL(getApiConfig().baseUrl);
	webSocket.pathname = `${webSocket.pathname.replace(/\/+$/, "")}/sync/ws/${encodeURIComponent(organizationId)}`;
	webSocket.search = "";
	webSocket.hash = "";
	webSocket.protocol = webSocket.protocol === "https:" ? "wss:" : "ws:";
	return webSocket.toString();
}

export function useOrgAuditLiveUpdates(
	organizationId: string | null | undefined,
): void {
	const queryClient = useQueryClient();
	const location = useLocation();
	const navigate = useNavigate();
	const { t } = useAppTranslation();

	const pathnameRef = useRef(location.pathname);
	const navigateRef = useRef(navigate);
	const translateRef = useRef(t);
	pathnameRef.current = location.pathname;
	navigateRef.current = navigate;
	translateRef.current = t;

	useEffect(() => {
		if (!organizationId || !GUID_PATTERN.test(organizationId)) return;
		if (typeof window === "undefined" || typeof WebSocket === "undefined") {
			return;
		}

		let stopped = false;
		let socket: WebSocket | null = null;
		let reconnectTimer: number | null = null;
		let batchTimer: number | null = null;
		let reconnectDelay = 1_000;
		const pendingMessages: AuditLogMessage[] = [];
		const webSocketUrl = liveUpdateWebSocketUrl(organizationId);

		const flush = () => {
			batchTimer = null;
			const messages = pendingMessages.splice(0);
			if (messages.length === 0) return;
			const currentUserId = getCurrentUser()?.id;
			void applyOrgAuditBatch(messages, {
				queryClient,
				currentUserId,
				pathname: pathnameRef.current,
				navigate: (href) => {
					void navigateRef.current({ href, replace: true });
				},
				deletedMessage: (display) =>
					translateRef.current("main.liveUpdates.entityDeleted", {
						name:
							display ||
							translateRef.current("main.liveUpdates.unnamedEntity"),
					}),
			});
		};

		const queueMessage = (message: AuditLogMessage) => {
			pendingMessages.push(message);
			if (batchTimer != null) window.clearTimeout(batchTimer);
			batchTimer = window.setTimeout(flush, BATCH_DELAY_MS);
		};

		const scheduleReconnect = () => {
			if (stopped || reconnectTimer != null) return;
			reconnectTimer = window.setTimeout(() => {
				reconnectTimer = null;
				connect();
			}, reconnectDelay);
			reconnectDelay = Math.min(
				reconnectDelay * 2,
				MAX_RECONNECT_DELAY_MS,
			);
		};

		const connect = () => {
			if (stopped) return;
			socket = new WebSocket(webSocketUrl);
			socket.onopen = () => {
				reconnectDelay = 1_000;
			};
			socket.onmessage = (event) => {
				if (typeof event.data !== "string") return;
				const parsed = parseAuditLogMessage(event.data);
				if (parsed) queueMessage(parsed);
			};
			socket.onclose = (event) => {
				socket = null;
				if (event.code === 1008) {
					stopped = true;
					return;
				}
				scheduleReconnect();
			};
			socket.onerror = () => {
				socket?.close();
			};
		};

		connect();

		return () => {
			stopped = true;
			if (reconnectTimer != null) window.clearTimeout(reconnectTimer);
			if (batchTimer != null) window.clearTimeout(batchTimer);
			pendingMessages.length = 0;
			socket?.close();
		};
	}, [organizationId, queryClient]);
}
